/**
 * React Query hook for the beam analysis endpoint.
 *
 * Calls POST /api/analysis/beams with the request from the Zustand store,
 * debounces it lightly so the user can type without flooding the API, and
 * exposes the response (cargas, fuerzas, diagrama, design_prefill).
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { BeamAnalysisRequest, BeamAnalysisResponse } from './types';

const DEBOUNCE_MS = 300;

export function useBeamAnalysisQuery(request: BeamAnalysisRequest) {
  // Debounce: only refetch when the request has been stable for DEBOUNCE_MS.
  const [debounced, setDebounced] = useState(request);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(request), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [request]);

  return useQuery<BeamAnalysisResponse>({
    queryKey: ['analysis', 'beams', debounced],
    queryFn: async () =>
      (await api.post<BeamAnalysisResponse>('/api/analysis/beams', debounced)).data,
    // Always have data once we have a response (no spinner while typing).
    placeholderData: (prev) => prev,
    // Don't retry: a 4xx is a validation issue and won't fix itself.
    retry: false,
    staleTime: 60_000,
  });
}
